import { UntitledIcon } from '@faceup/icons'
import { ulCheck } from '@faceup/icons/ulCheck'
import { useIntl } from '@faceup/localization'
import { trpc, useSuspenseQuery } from '@faceup/trpc'
import { Card, ContentLayout, PageContentSkeleton } from '@faceup/ui'
import { Table, type TableColumns, Tag, Typography } from '@faceup/ui-base'
import { convertRegionToRegionName, FuAdminRoleType } from '@faceup/utils'
import { Suspense } from 'react'
import { TrpcProvider } from '../../TrpcProvider'
import { fuAdminAccess, fuAdminRoleLabels } from '../../utils/fuAdminAccess'
import { DEFAULT_SUPPORTED_REGION, useRegion } from '../../utils/useRegion'

const { Text } = Typography

const roles = Object.values(FuAdminRoleType)

const accessAreas = Object.entries(fuAdminAccess).map(([key, area]) => ({ key, ...area }))

type AccessArea = (typeof accessAreas)[number]

export const InternalUsersView = () => {
  const { region } = useRegion()
  const activeRegion = region ?? DEFAULT_SUPPORTED_REGION

  return (
    <ContentLayout header={<ContentLayout.Header title='Internal users' />}>
      <Text type='secondary'>
        Internal users and their roles in the {convertRegionToRegionName(activeRegion)} region.
        Roles are assigned with SQL in every region; switch the region in Settings to compare them.
      </Text>
      <TrpcProvider region={activeRegion}>
        <Suspense fallback={<PageContentSkeleton />}>
          <InternalUsers />
        </Suspense>
      </TrpcProvider>
    </ContentLayout>
  )
}

const InternalUsers = () => {
  const { formatDate } = useIntl()
  const { data: fuAdmins } = useSuspenseQuery(trpc.fuAdmin.list.queryOptions())

  const countUsersWithRole = (role: FuAdminRoleType) =>
    fuAdmins.filter(fuAdmin => fuAdmin.roles.includes(role)).length

  const roleColumns: TableColumns<AccessArea> = roles.map(role => ({
    title: `${fuAdminRoleLabels[role]} · ${countUsersWithRole(role)}`,
    key: role,
    align: 'center',
    render: area =>
      area.roles.some(areaRole => areaRole === role) ? (
        <span className='text-colorSuccess'>
          <UntitledIcon icon={ulCheck} aria-hidden />
          <span className='sr-only'>Allowed</span>
        </span>
      ) : (
        <span className='text-colorTextQuaternary'>
          <span aria-hidden>–</span>
          <span className='sr-only'>Not allowed</span>
        </span>
      ),
  }))

  return (
    <div className='mt-4 flex flex-col gap-6'>
      <Card>
        <Card.Header hint='An admin with several roles gets everything any of them unlocks.'>
          What each role unlocks
        </Card.Header>
        <Card.Content>
          <Table
            rowKey='key'
            dataSource={accessAreas}
            pagination={false}
            scroll={{ x: 'max-content' }}
            columns={[
              {
                title: 'Page or action',
                key: 'area',
                render: area => area.label,
              },
              {
                title: 'Type',
                key: 'kind',
                render: area => (
                  <Text type='secondary'>{area.kind === 'page' ? 'Page' : 'Action'}</Text>
                ),
              },
              ...roleColumns,
            ]}
          />
        </Card.Content>
      </Card>
      <Card>
        <Card.Header>Users · {fuAdmins.length}</Card.Header>
        <Card.Content>
          <Table
            rowKey='id'
            dataSource={fuAdmins}
            pagination={false}
            scroll={{ x: 'max-content' }}
            columns={[
              {
                title: 'Name',
                key: 'name',
                render: fuAdmin => fuAdmin.name,
              },
              {
                title: 'Email',
                key: 'email',
                render: fuAdmin => fuAdmin.email,
              },
              {
                title: 'Roles',
                key: 'roles',
                render: fuAdmin => (
                  <div className='flex flex-wrap gap-1'>
                    {fuAdmin.roles.map(role => (
                      <Tag key={role}>{fuAdminRoleLabels[role]}</Tag>
                    ))}
                  </div>
                ),
              },
              {
                title: 'Last login',
                key: 'lastLogin',
                render: fuAdmin =>
                  fuAdmin.lastLogin ? (
                    formatDate(fuAdmin.lastLogin, { dateStyle: 'medium', timeStyle: 'short' })
                  ) : (
                    <Text type='secondary'>Never</Text>
                  ),
              },
            ]}
          />
        </Card.Content>
      </Card>
    </div>
  )
}
